import styles from './SpentRemainingRow.module.scss';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import LiteralsService from 'src/services/literals/LiteralsService';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';
import FormatService from 'src/services/format.service';
import { getCategoryTransactionsUrl, goToBudgetCategoryTransactions } from 'src/services/router.service';
import React from 'react';
import { useHistory } from 'react-router-dom';
import adobeTags from 'src/types/adobe-tags';
import { BudgetSummaryStatus } from 'src/types/budget-summary-status.enum';
import Selectors from 'src/utils/selectors';
import { useBreakpoints } from 'src/hooks/useBreakpoints';
import { useSyncingAccounts } from 'src/hooks/useSyncingAccounts';
import SkeletonLoader from 'src/components/atomic/atoms/skeletonLoader/SkeletonLoader';

export interface SpentRemainingRowProps {
    spentAmount: number;
    remainingAmount: number;
    budgetGroupId: string;
    budgetGroupName: string;
    status: BudgetSummaryStatus;
    tagName: string;
}

export default function SpentRemainingRow({
    spentAmount,
    remainingAmount,
    budgetGroupId,
    budgetGroupName,
    status,
    tagName
}: SpentRemainingRowProps): JSX.Element {
    const { isMinWidthMedium } = useBreakpoints();
    const { isSyncingAccounts } = useSyncingAccounts();
    const history = useHistory();
    const statusClassName: string =
        status === BudgetSummaryStatus.OVER
            ? styles.bad
            : status === BudgetSummaryStatus.AT_RISK
            ? styles.warning
            : styles.good;

    const amountSpent: JSX.Element = isSyncingAccounts ? (
        <div className={`${styles.amountSkeleton}`}>
            <SkeletonLoader className={styles.spentRow1Skeleton} />
            <SkeletonLoader className={styles.spentRow2Skeleton} />
        </div>
    ) : (
        <div className={styles.amountSpent}>
            <Typography variant={TypographyVariants.BODY_BOLD}>
                {LiteralsService.getLiteral('Budget.Current.spent')}
            </Typography>
            <Anchor
                onClick={() => goToBudgetCategoryTransactions(false, history, budgetGroupId)}
                tag={`${tagName}-${adobeTags.CurrentMonth.Spend}`}
                href={getCategoryTransactionsUrl(budgetGroupId)}
                typographyProps={{ variant: TypographyVariants.BODY_BOLD }}
                className={styles.spentAmount}
                data-cy={Selectors.SpendSmart.AMOUNT_SPENT}
                aria-label={LiteralsService.getLiteral('Budget.Current.viewTransactionsLinkDescription', [
                    budgetGroupName
                ])}
            >
                {FormatService.formatCurrency(spentAmount)}
            </Anchor>
        </div>
    );

    const amountRemaining: JSX.Element = isSyncingAccounts ? (
        <div className={`${styles.amountSkeleton} ${styles.endAligned}`}>
            <SkeletonLoader className={styles.remainingRow1Skeleton} />
            <SkeletonLoader className={styles.remainingRow2Skeleton} />
        </div>
    ) : (
        <div className={styles.amountRemaining} data-cy={Selectors.SpendSmart.AMOUNT_REMAINING}>
            <Typography
                variant={isMinWidthMedium ? TypographyVariants.BODY : TypographyVariants.BODY_BOLD}
                data-cy={Selectors.SpendSmart.AMOUNT_REMAINING_TITLE}
            >
                {LiteralsService.getLiteral(
                    remainingAmount < 0 ? 'Budget.Current.exceeding' : 'Budget.Current.remaining'
                )}
            </Typography>
            <Typography
                variant={TypographyVariants.BODY_BOLD}
                className={`${styles.progressAmountPill} ${statusClassName}`}
                data-cy={Selectors.SpendSmart.AMOUNT_REMAINING_PILL}
            >
                {FormatService.formatCurrency(remainingAmount < 0 ? remainingAmount * -1 : remainingAmount)}
            </Typography>
        </div>
    );

    return (
        <div className={styles.spentRemainingRow}>
            {amountSpent}
            {amountRemaining}
        </div>
    );
}
